import React from "react";
import { loadMunicipiosGeojson } from "./geojson";
import type { Municipality, Region } from "./types";

// Mini-mapa do canto da barra lateral: versao compacta do mapa coropletico da
// aba Mapa. Pinta cada municipio (malha IBGE do public/municipios.geojson) com
// a cor da sua regiao comercial. Usa Leaflet + OpenStreetMap via CDN (sem chave)
// com renderizacao em canvas (aguenta os 5.570 municipios num quadrado pequeno).
// Selecionar uma regiao destaca a area dela e enquadra o mapa; clicar numa area
// pintada seleciona/desmarca aquela regiao.

declare const L: any;

const DEFAULT_COLOR = "#64748b";
const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

let leafletPromise: Promise<void> | null = null;

function loadLeaflet(): Promise<void> {
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise<void>((resolve, reject) => {
    if (typeof L !== "undefined") return resolve();
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Falha ao carregar o Leaflet"));
    document.head.appendChild(script);
  });
  return leafletPromise;
}

export function NucleosMiniMap({
  municipalities,
  regions,
  selectedRegionId,
  onSelectRegion,
}: {
  municipalities: Municipality[];
  regions: Region[];
  selectedRegionId: number | null;
  onSelectRegion: (regionId: number) => void;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<any>(null);
  const layerRef = React.useRef<any>(null);
  const [ready, setReady] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  const colorByCode = React.useMemo(() => {
    const colorByRegion = new Map(regions.map((region) => [region.id, region.color_hex]));
    const map = new Map<string, string>();
    for (const city of municipalities) {
      map.set(city.ibge_code, colorByRegion.get(city.region_id) || DEFAULT_COLOR);
    }
    return map;
  }, [municipalities, regions]);

  const regionByCode = React.useMemo(() => {
    const map = new Map<string, number>();
    for (const city of municipalities) map.set(city.ibge_code, city.region_id);
    return map;
  }, [municipalities]);

  // Refs para a funcao de estilo/clique sempre lerem a versao mais recente.
  const colorRef = React.useRef(colorByCode);
  colorRef.current = colorByCode;
  const regionRef = React.useRef(regionByCode);
  regionRef.current = regionByCode;
  const selectedRef = React.useRef(selectedRegionId);
  selectedRef.current = selectedRegionId;
  const onSelectRef = React.useRef(onSelectRegion);
  onSelectRef.current = onSelectRegion;

  const styleFor = React.useCallback((feature: any) => {
    const code = String(feature.properties.c);
    const regionId = regionRef.current.get(code) ?? null;
    const color = colorRef.current.get(code) || DEFAULT_COLOR;
    const sel = selectedRef.current;
    const isTarget = sel == null || regionId === sel;
    return {
      fillColor: color,
      fillOpacity: isTarget ? 0.82 : 0.06,
      color,
      weight: 0.25,
      opacity: isTarget ? 0.5 : 0.08,
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    Promise.all([loadLeaflet(), loadMunicipiosGeojson()])
      .then(([, geojson]) => {
        if (cancelled || !containerRef.current || mapRef.current) return;
        const map = L.map(containerRef.current, {
          attributionControl: false,
          zoomControl: false,
          preferCanvas: true,
        }).setView([-29.5, -53], 5);
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 12,
          minZoom: 3,
        }).addTo(map);
        L.control.zoom({ position: "bottomright" }).addTo(map);

        const layer = L.geoJSON(geojson, {
          style: styleFor,
          onEachFeature: (_feature: any, lyr: any) => {
            lyr.on("click", () => {
              const code = String(lyr.feature.properties.c);
              const regionId = regionRef.current.get(code);
              if (regionId != null) onSelectRef.current(regionId);
            });
          },
        }).addTo(map);

        mapRef.current = map;
        layerRef.current = layer;
        if (!cancelled) setReady(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [styleFor]);

  // Reaplica cores quando a divisao de regioes muda.
  React.useEffect(() => {
    if (ready && layerRef.current) layerRef.current.setStyle(styleFor);
  }, [ready, colorByCode, styleFor]);

  // Aplica o destaque e enquadra a regiao selecionada.
  React.useEffect(() => {
    if (!ready || !layerRef.current || !mapRef.current) return;
    const layer = layerRef.current;
    const map = mapRef.current;
    layer.setStyle(styleFor);

    if (selectedRegionId == null) {
      map.setView([-29.5, -53], 5, { animate: true });
      return;
    }
    let bounds: any = null;
    layer.eachLayer((lyr: any) => {
      const code = String(lyr.feature.properties.c);
      if (regionByCode.get(code) === selectedRegionId) {
        bounds = bounds ? bounds.extend(lyr.getBounds()) : L.latLngBounds(lyr.getBounds());
      }
    });
    if (bounds) map.fitBounds(bounds.pad(0.15), { maxZoom: 9, animate: true });
  }, [ready, selectedRegionId, regionByCode, styleFor]);

  return (
    <div className="mini-map">
      <div ref={containerRef} className="mini-map__canvas" />
      {!ready && !failed ? <div className="mini-map__overlay">Carregando mapa…</div> : null}
      {failed ? <div className="mini-map__overlay">Mapa indisponível</div> : null}
    </div>
  );
}
